import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { Box, Chip, Typography } from "@mui/joy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { SectionCitation } from "../../types/citations";

interface CitationTooltipProps {
  citations: Record<number, SectionCitation[]>;
  isPanelOpen: boolean;
  containerRef?: React.RefObject<HTMLElement>;
}

const TOOLTIP_OFFSET_Y = 8;
const VIEWPORT_PADDING = 8;

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  citations,
  isPanelOpen,
  containerRef,
}) => {
  const [tooltip, setTooltip] = useState<{
    citation: SectionCitation;
    x: number;
    y: number;
  } | null>(null);
  const [clampedPos, setClampedPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseOver = useCallback(
    (e: MouseEvent) => {
      if (!isPanelOpen) return;
      const target = e.target as HTMLElement;
      const refSpan = target.closest("[data-ref]") as HTMLElement | null;
      if (!refSpan) return;

      const refId = refSpan.getAttribute("data-ref");
      if (!refId) return;

      const sectionEl = refSpan.closest(
        "[data-section-id]",
      ) as HTMLElement | null;
      if (!sectionEl) return;

      const sectionId = Number(sectionEl.getAttribute("data-section-id"));
      if (isNaN(sectionId)) return;

      const sectionCitations = citations[sectionId];
      if (!sectionCitations) return;

      const citation = sectionCitations.find((c) => c.ref_id === refId);
      if (!citation) return;

      const rect = refSpan.getBoundingClientRect();
      setTooltip({
        citation,
        x: rect.left + rect.width / 2,
        y: rect.bottom + TOOLTIP_OFFSET_Y,
      });
    },
    [citations, isPanelOpen],
  );

  const handleMouseOut = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    // Don't hide if moving to the tooltip itself
    if (
      tooltipRef.current &&
      relatedTarget &&
      tooltipRef.current.contains(relatedTarget)
    ) {
      return;
    }

    const refSpan = target.closest("[data-ref]");
    if (refSpan) {
      setTooltip(null);
    }
  }, []);

  useEffect(() => {
    const target = containerRef?.current ?? document;
    target.addEventListener("mouseover", handleMouseOver as EventListener);
    target.addEventListener("mouseout", handleMouseOut as EventListener);
    return () => {
      target.removeEventListener("mouseover", handleMouseOver as EventListener);
      target.removeEventListener("mouseout", handleMouseOut as EventListener);
    };
  }, [handleMouseOver, handleMouseOut, containerRef]);

  // Hide tooltip on scroll
  useEffect(() => {
    if (!tooltip) return;
    const handleScroll = () => setTooltip(null);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [tooltip]);

  // Clamp tooltip position to viewport after render
  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) {
      setClampedPos(null);
      return;
    }
    const el = tooltipRef.current;
    const tooltipWidth = el.offsetWidth;
    const tooltipHeight = el.offsetHeight;

    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(
        tooltip.x - tooltipWidth / 2,
        window.innerWidth - tooltipWidth - VIEWPORT_PADDING,
      ),
    );
    const top = Math.max(
      VIEWPORT_PADDING,
      Math.min(
        tooltip.y,
        window.innerHeight - tooltipHeight - VIEWPORT_PADDING,
      ),
    );
    setClampedPos({ left, top });
  }, [tooltip]);

  if (!tooltip) return null;

  const { citation, x, y } = tooltip;
  const isSourceType = citation.source_type === "Source";
  const isVerified = !isSourceType || citation.verified;
  const passagePreview =
    citation.exact_passage.length > 120
      ? citation.exact_passage.slice(0, 120) + "..."
      : citation.exact_passage;

  return (
    <Box
      ref={tooltipRef}
      onMouseLeave={() => setTooltip(null)}
      sx={{
        position: "fixed",
        left: clampedPos ? clampedPos.left : x,
        top: clampedPos ? clampedPos.top : y,
        transform: clampedPos ? "none" : "translateX(-50%)",
        zIndex: 9999,
        backgroundColor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        padding: "10px 14px",
        maxWidth: 340,
        minWidth: 200,
        pointerEvents: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <Chip
          size="sm"
          variant="soft"
          color={isVerified ? "success" : "warning"}
          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
        >
          {citation.ref_id}
        </Chip>
        <Typography level="body-xs" fontWeight="lg" sx={{ flex: 1 }}>
          {citation.source_type === "Internal"
            ? `Internal ref: ${citation.reference_section_title || "section"}`
            : `${citation.source_filename}${citation.page != null ? `, p.${citation.page}` : ""}`}
        </Typography>
        {isSourceType ? (
          isVerified ? (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 14, color: "success.500" }}
            />
          ) : (
            <WarningAmberIcon sx={{ fontSize: 14, color: "warning.500" }} />
          )
        ) : null}
      </Box>
      <Typography
        level="body-xs"
        sx={{ color: "neutral.600", lineHeight: 1.5, fontStyle: "italic" }}
      >
        {passagePreview}
      </Typography>
      {isSourceType && citation.verification_reason && (
        <Typography
          level="body-xs"
          sx={{
            color: isVerified ? "success.600" : "warning.600",
            lineHeight: 1.4,
            mt: 0.5,
            fontSize: "0.65rem",
          }}
        >
          {citation.verification_reason}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(CitationTooltip);
